import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../../context/ThemeContext';

const ResultsContainer = styled.div`
  background-color: var(--card-bg-color);
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 1rem;
  color: var(--text-color);
  max-width: 780px;
  margin-left: auto;
  margin-right: auto;
  transition: background-color 0.3s ease, color 0.3s ease;
`;

const ResultsTitle = styled.h2`
  font-size: 1.125rem;
  margin-bottom: 1.5rem;
  color: var(--text-color);
  transition: color 0.3s ease;
  font-weight: 600;
`;

const ResultSection = styled.div`
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h3`
  font-size: 1rem;
  margin-bottom: 0.75rem;
  color: var(--text-color);
  border-bottom: 1px solid var(--input-border-color);
  padding-bottom: 0.25rem;
  transition: color 0.3s ease, border-color 0.3s ease;
`;

const ResultsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;
  max-width: 100%;
  
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ResultItem = styled.div`
  padding: 0.5rem;
  background-color: ${props => props.theme.mode === 'dark' ? '#2a2a2a' : '#f9f9f9'};
  border-radius: 8px;
  border: 1px solid var(--input-border-color);
  transition: background-color 0.3s ease, border-color 0.3s ease;
`;

const ResultLabel = styled.div`
  font-size: ${props => props.isPrice ? '1.1rem' : '0.85rem'};
  color: ${props => {
    if (props.isCall) {
      return props.theme.mode === 'dark' ? '#90caf9' : '#4a90e2';
    } else {
      return props.theme.mode === 'dark' ? '#ffb74d' : '#e67e22';
    }
  }};
  font-weight: ${props => props.isPrice ? '600' : '400'};
  margin-bottom: 0.25rem;
  transition: color 0.3s ease;
`;

const ResultValue = styled.div`
  font-size: ${props => props.isPrice ? '1.5rem' : '0.9rem'};
  font-weight: ${props => props.isPrice ? '600' : '500'};
  color: var(--text-color);
  transition: color 0.3s ease;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: ${props => props.theme.mode === 'dark' ? '#b0b0b0' : '#555'};
  font-style: italic;
  transition: color 0.3s ease;
`;

const ResultsDisplay = ({
  results,
  greeks,
  isLoading
}) => {
  const { theme } = useTheme();
  if (isLoading) {
    return (
      <ResultsContainer>
        <ResultsTitle>Results</ResultsTitle>
        <LoadingMessage theme={theme}>Calculating...</LoadingMessage>
      </ResultsContainer>
    );
  }
  
  if (!results) {
    return (
      <ResultsContainer>
        <ResultsTitle>Results</ResultsTitle>
        <LoadingMessage theme={theme}>Enter parameters to calculate option price</LoadingMessage>
      </ResultsContainer>
    );
  }
  
  const formatValue = (value, type = '') => {
    if (value === null || value === undefined) return '-';  
    
    if (type === 'price' || type === 'dollar') {
      return `$${value.toFixed(2)}`;
    } else if (type === 'percent') {
      return `${(value * 100).toFixed(2)}%`;
    } else if (type === 'decimal') {
      return value.toFixed(4);
    }
    
    return value.toFixed(4);
  };
  

  
  return (
    <ResultsContainer>
      <ResultsTitle>Results</ResultsTitle>
      
      <ResultsGrid>
        <div>
          <ResultItem theme={theme}>
            <ResultLabel isPrice isCall theme={theme}>Call Option Price</ResultLabel>
            <ResultValue isPrice theme={theme}>{formatValue(results.call_price, 'price')}</ResultValue>
          </ResultItem>
          
          {greeks && (
            <div style={{ marginTop: '1rem' }}>
              <ResultsGrid style={{ gap: '0.5rem' }}>
                <ResultItem theme={theme}>
                  <ResultLabel isCall theme={theme}>Delta</ResultLabel>
                  <ResultValue theme={theme}>{formatValue(greeks.call.delta, 'decimal')}</ResultValue>
                </ResultItem>
                <ResultItem theme={theme}>
                  <ResultLabel isCall theme={theme}>Gamma</ResultLabel>
                  <ResultValue theme={theme}>{formatValue(greeks.call.gamma, 'decimal')}</ResultValue>
                </ResultItem>
                <ResultItem theme={theme}>
                  <ResultLabel isCall theme={theme}>Theta</ResultLabel>
                  <ResultValue theme={theme}>{formatValue(greeks.call.theta, 'dollar')}</ResultValue>
                </ResultItem>
                <ResultItem theme={theme}>
                  <ResultLabel isCall theme={theme}>Vega</ResultLabel>
                  <ResultValue theme={theme}>{formatValue(greeks.call.vega, 'dollar')}</ResultValue>
                </ResultItem>
                <ResultItem theme={theme}>
                  <ResultLabel isCall theme={theme}>Rho</ResultLabel>
                  <ResultValue theme={theme}>{formatValue(greeks.call.rho, 'dollar')}</ResultValue>
                </ResultItem>
              </ResultsGrid>
            </div>
          )}
        </div>
        
        <div>
          <ResultItem theme={theme}>
            <ResultLabel isPrice theme={theme}>Put Option Price</ResultLabel>
            <ResultValue isPrice theme={theme}>{formatValue(results.put_price, 'price')}</ResultValue>
          </ResultItem>
          
          {greeks && (
            <div style={{ marginTop: '1rem' }}>
              <ResultsGrid style={{ gap: '0.5rem' }}>
                <ResultItem theme={theme}>
                  <ResultLabel theme={theme}>Delta</ResultLabel>
                  <ResultValue theme={theme}>{formatValue(greeks.put.delta, 'decimal')}</ResultValue>
                </ResultItem>
                <ResultItem theme={theme}>
                  <ResultLabel theme={theme}>Gamma</ResultLabel>
                  <ResultValue theme={theme}>{formatValue(greeks.put.gamma, 'decimal')}</ResultValue>
                </ResultItem>
                <ResultItem theme={theme}>
                  <ResultLabel theme={theme}>Theta</ResultLabel>
                  <ResultValue theme={theme}>{formatValue(greeks.put.theta, 'dollar')}</ResultValue>
                </ResultItem>
                <ResultItem theme={theme}>
                  <ResultLabel theme={theme}>Vega</ResultLabel>
                  <ResultValue theme={theme}>{formatValue(greeks.put.vega, 'dollar')}</ResultValue>
                </ResultItem>
                <ResultItem theme={theme}>
                  <ResultLabel theme={theme}>Rho</ResultLabel>
                  <ResultValue theme={theme}>{formatValue(greeks.put.rho, 'dollar')}</ResultValue>
                </ResultItem>
              </ResultsGrid>
            </div>
          )}
        </div>
      </ResultsGrid>
    </ResultsContainer>
  );
};

export default ResultsDisplay;
